/**
 * services/cash/cash.service.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Assignments, the append-only ledger, and the custodian's own two views.
 *
 * There is no `updateTransaction` and no `deleteTransaction` in this file, and
 * there never will be: a correction is `reverseTransaction`, which appends a
 * new row pointing at the one it corrects (invariant 1). The API has no PATCH
 * or DELETE on a transaction either, so this is not a client-side convention —
 * it is the contract.
 * ──────────────────────────────────────────────────────────────────────────
 */
import axiosInstance from '../../api/axiosInstance';
import {
  Cash_Assignment_Close_Purpose_Url,
  Cash_Assignment_Evidence_Url,
  Cash_Assignment_Transfer_Url,
  Cash_Assignment_Url,
  Cash_Assignments_Url,
  Cash_My_Assignments_Url,
  Cash_My_Transactions_Url,
  Cash_Transaction_Reverse_Url,
  Cash_Transactions_Url,
} from '../../api/endpoints';
import { parseResponse } from '../../schemas/common';
import {
  assignmentDetailSchema,
  assignmentSchema,
  assignmentsResponseSchema,
  recordResultSchema,
  transactionsResponseSchema,
  type Assignment,
  type AssignmentCreate,
  type AssignmentDetail,
  type RecordResult,
  type Scope,
  type Transaction,
  type TransactionCreate,
  type TransferInput,
} from '../../schemas/cash';

/** Server-side filters for the register. `q` searches holder and reference. */
export interface AssignmentFilters {
  scope?: Scope;
  status?: 'active' | 'suspended' | 'closed';
  holder_ref?: string;
  jurisdiction?: string;
  q?: string;
  limit?: number;
}

/**
 * The assignment register. Filtering happens server-side so the "stale" flag
 * on every row is computed once, from the org's own staleDays, and every row on
 * the page therefore agrees on what stale means.
 */
export async function fetchAssignments(filters: AssignmentFilters = {}): Promise<Assignment[]> {
  const { data } = await axiosInstance.get(Cash_Assignments_Url, { params: filters });
  return parseResponse(assignmentsResponseSchema, data, 'GET /v1/cash/assignments').assignments;
}

/** The "Assign cash" modal. */
export async function createAssignment(input: AssignmentCreate): Promise<{ id?: string }> {
  const { data } = await axiosInstance.post(Cash_Assignments_Url, input);
  return data as { id?: string };
}

/**
 * Everything the ledger drawer needs, in ONE request: the assignment, its whole
 * ledger and its custody history. Opening a drawer is one interaction, so it is
 * one round trip.
 */
export async function fetchAssignmentDetail(id: string): Promise<AssignmentDetail> {
  const { data } = await axiosInstance.get(Cash_Assignment_Url(id));
  return parseResponse(assignmentDetailSchema, data, 'GET /v1/cash/assignments/:id');
}

/* ── The ledger ──────────────────────────────────────────────────────────── */

/**
 * Record a spend, collection, deposit or replenishment.
 *
 * The result may carry an `approval`: the engine held the spend and nothing has
 * moved yet. Callers must say so rather than confirming a transfer that has not
 * happened — see the spend modal, which reads `result.approval` to choose
 * between "Spend recorded" and "Sent for approval".
 */
export async function recordTransaction(input: TransactionCreate): Promise<RecordResult> {
  const { data } = await axiosInstance.post(Cash_Transactions_Url, input);
  return parseResponse(recordResultSchema, data, 'POST /v1/cash/transactions');
}

export async function fetchTransactions(assignmentId: string, limit?: number): Promise<Transaction[]> {
  const { data } = await axiosInstance.get(Cash_Transactions_Url, {
    params: { assignment_id: assignmentId, limit },
  });
  return parseResponse(transactionsResponseSchema, data, 'GET /v1/cash/transactions').transactions;
}

/**
 * The ONLY way to correct the ledger. Appends a reversing row; the original
 * stays, which is what makes the history auditable. A row can be reversed once,
 * enforced by a unique index — so a double-clicked Reverse yields one reversal.
 */
export async function reverseTransaction(id: string, memo?: string): Promise<{ id?: string }> {
  const { data } = await axiosInstance.post(Cash_Transaction_Reverse_Url(id), memo ? { memo } : {});
  return data as { id?: string };
}

/* ── Managing the assignment ─────────────────────────────────────────────── */

/** Hand custody to someone else. The balance moves with the assignment. */
export async function transferCustody(
  assignmentId: string,
  input: TransferInput,
): Promise<{ id?: string }> {
  const { data } = await axiosInstance.post(Cash_Assignment_Transfer_Url(assignmentId), input);
  return data as { id?: string };
}

/**
 * Close a purpose assignment. What happens to the unspent balance is decided by
 * the assignment's own `end_rule`, set when it was created — not by whoever
 * closes it.
 */
export async function closePurpose(assignmentId: string, reason?: string): Promise<unknown> {
  const { data } = await axiosInstance.post(
    Cash_Assignment_Close_Purpose_Url(assignmentId),
    reason ? { reason } : {},
  );
  return data;
}

/**
 * Mint a presigned upload for evidence. The browser never talks to Drive
 * directly; it PUTs to the URL this returns and then puts `document_ref` on the
 * transaction. The presence of that reference is what the `noReceipt` approval
 * rule tests.
 */
export async function mintEvidenceUpload(
  assignmentId: string,
  filename: string,
  contentType: string,
): Promise<{ upload_url: string; document_ref: string }> {
  const { data } = await axiosInstance.post(Cash_Assignment_Evidence_Url(assignmentId), {
    filename,
    content_type: contentType,
  });
  return data as { upload_url: string; document_ref: string };
}

/* ── Custodian console ───────────────────────────────────────────────────── */

/**
 * "My cash" and "My transactions". Both are scoped by the holder reference in
 * the caller's own token — never by a query parameter — so a custodian cannot
 * widen them to the whole org.
 */
export async function fetchMyAssignments(): Promise<Assignment[]> {
  const { data } = await axiosInstance.get(Cash_My_Assignments_Url);
  return parseResponse(assignmentsResponseSchema, data, 'GET /v1/cash/my/assignments').assignments;
}

export async function fetchMyTransactions(): Promise<Transaction[]> {
  const { data } = await axiosInstance.get(Cash_My_Transactions_Url);
  return parseResponse(transactionsResponseSchema, data, 'GET /v1/cash/my/transactions')
    .transactions;
}

/** Re-exported so screens type their state without a second import path. */
export type { Assignment, Transaction, AssignmentDetail };
export { assignmentSchema };
